<template>
    <VVVLayoutNew>
        <template #page-title>
            Early Payout
        </template>
        <MDBCard class="shadow-0 card-container">
            <MDBCardHeader class="d-flex align-items-center justify-content-end gap-3">
                <div class="d-flex gap-3 align-items-center" :style="isMobile ? 'width: 100%' : 'width: 15%'">
                    <div class="did-floating-label-content w-100">
                        <select v-model="filterByStatus" class="did-floating-select">
                            <option v-for="option in accountStatusFilter" :key="option.value" :value="option.value">
                                {{ option.text }}
                            </option>
                        </select>
                        <label class="did-floating-label">Filter by Subscription Status</label>
                    </div>
                </div>
                <div class="w-auto">
                    <MDBInput inputGroup="md" :formOutline="false" v-model="searchValue" aria-describedby="search-addon"
                        aria-label="Search" placeholder="Enter to Search" @keydown.enter="handleSearch">
                        <span class="input-group-text" @click="handleSearch"><em class="fas fa-search" /></span>
                    </MDBInput>
                </div>
            </MDBCardHeader>
            <MDBCardBody class="d-flex flex-column p-0">
                <div class="overflow-auto mt-3" id="table-container">
                    <MDBTable hover class="align-middle">
                        <thead class="table-light">
                            <tr>
                                <th>User</th>
                                <th>Date Updated</th>
                                <th>Credit Status</th>
                                <th>Subscription Status</th>
                                <th class="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="loadingSpinner">
                                <tr>
                                    <td colspan="5" class="text-center">
                                        <div class="image-container d-flex justify-content-center align-items-center">
                                            <div class="loader"></div>
                                            <img src="@/assets/VVV/vvv-loader.png" alt="Loading image">
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr v-if="subscriptionList.data.length === 0 && !loadingSpinner">
                                    <td colspan="5" class="text-center">No subscription found.</td>
                                </tr>
                                <template v-else v-for="(item, i) in subscriptionList.data" :key="i">
                                    <VVVPayoutTableRow :item="item" :getUsers="getUsers"
                                        @trigger-success-toast="handleSuccessToast"
                                        @trigger-error-toast="handleErrorToast" :openDropdownId="openDropdownId"
                                        @toggleDropdown="handleToggleDropdown" />
                                </template>
                            </template>
                        </tbody>
                    </MDBTable>
                </div>
                <div class="overflow-auto">
                    <Paginate v-if="subscriptionList.length != 0 && subscriptionList.pagination?.totalPages"
                        :page-count="subscriptionList.pagination.totalPages" :click-handler="handlePage"
                        :prev-text="'Previous'" :next-text="'Next'" :container-class="'pagination'" first-last-button
                        style="margin-top: 0px; margin-left: unset; overflow: auto;">
                    </Paginate>
                </div>
            </MDBCardBody>
        </MDBCard>
        <MDBToast v-model="successToast.show" id="basic-success-example" autohide position="top-right" appendToBody
            stacking width="350px" color="success" text="white" icon="fas fa-info-circle fa-lg me-2">
            <template #title>{{ successToast.title }}</template>
            {{ successToast.message }}
        </MDBToast>
        <MDBToast v-model="errorToast.show" id="basic-danger-example" autohide position="top-right" appendToBody
            stacking width="350px" color="danger" text="white" icon="fas fa-exclamation-triangle fa-lg me-2">
            <template #title>{{ errorToast.title }}</template>
            {{ errorToast.message }}
        </MDBToast>
    </VVVLayoutNew>
</template>

<script setup>
import { GetSubscriptionList } from "@/services/Subscriptions/GetSubscriptionList";
import { useTitle } from "@vueuse/core";
import { MDBCard, MDBCardBody, MDBCardHeader, MDBInput, MDBTable, MDBToast } from "mdb-vue-ui-kit";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Paginate from "vuejs-paginate-next";
import VVVLayoutNew from "../VVVLayoutNew.vue";
import VVVPayoutTableRow from "./VVVPayoutTableRow.vue";

useTitle("Early Pay | Very Viral Videos");

const isMobile = ref(window.innerWidth <= 767);
const updateResolution = () => {
    isMobile.value = window.innerWidth <= 767;
};

onMounted(() => {
    window.addEventListener('resize', updateResolution);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateResolution);
});

const subscriptionList = ref({
    data: [],
    pagination: {},
});

const selectedUserTypeFilter = ref(null);

const filterByStatus = ref(null);
const accountStatusFilter = ref([
    { value: null, text: "All" },
    { value: true, text: "Activated" },
    { value: false, text: "Deactivated" },
]);

const errorToast = ref({
    title: 'Success',
    show: false,
    message: ""
})

const successToast = ref({
    title: 'Error',
    show: false,
    message: ""
})

const handleSuccessToast = ({ show, title, message }) => {
    successToast.value.show = show;
    successToast.value.title = title;
    successToast.value.message = message;
};

const handleErrorToast = ({ show, title, message }) => {
    errorToast.value.show = show;
    errorToast.value.title = title;
    errorToast.value.message = message;
};

const currentPageNumber = ref(null)

const loadingSpinner = ref(false);
const searchValue = ref('');

const getUsers = async () => {
    loadingSpinner.value = true;
    const params = {};

    if (searchValue.value) {
        params.searchString = searchValue.value;
    }

    if (filterByStatus.value !== null) {
        params.isEnabled = filterByStatus.value;
    }

    if (currentPageNumber.value !== null) {
        params.pageNumber = currentPageNumber.value;
    }

    try {
        const response = await GetSubscriptionList(params);
        subscriptionList.value.data = response.data || [];
        subscriptionList.value.pagination = response.pagination || {};
        loadingSpinner.value = false;
    } catch (error) {
        console.error("Error fetching subscriptions: ", error);
        errorToast.value.show = true
        errorToast.value.title = "Error"
        errorToast.value.show = `Error fetching subscriptionds: ${error}`
        loadingSpinner.value = false;
    }
};

onMounted(() => {
    getUsers();
});

watch([selectedUserTypeFilter, filterByStatus], () => {
    getUsers();
});

const handleSearch = async () => {
    getUsers();
}

const handlePage = (currentPage) => {
    event.preventDefault();
    currentPageNumber.value = currentPage
    getUsers();
}

const openDropdownId = ref(null);

const handleToggleDropdown = (id) => {
    openDropdownId.value = openDropdownId.value === id ? null : id;
};
</script>

<style scoped>
.btn-primary {
    background-color: var(--primary);
}

.btn-outline-primary {
    border-color: var(--accent);
    color: var(--accent);
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.2), 0 2px 10px 0 rgba(0, 0, 0, 0.1);
}

.table-dark {
    --mdb-table-bg: var(--accent);
    border-color: var(--accent);
}
</style>

<style>
.user-management-loader {
    color: var(--accent);
}

th {
    text-transform: uppercase;
}

tr {
    cursor: default;
}

.card-container {
    flex: 1 1 auto;
    overflow: auto;
}

#table-container {
    flex: 1 1 auto;
    height: 55vh;
}

.image-container {
    position: relative;
    display: inline-block;
}

.image-container .loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    width: 45px;
    height: 45px;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: BorderSpin 1s linear infinite;
}

.image-container img {
    display: block;
    width: 45px;
    height: 45px;
    border-radius: 50%;
}

@keyframes BorderSpin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* customized select with floating label */
.did-floating-label-content {
    position: relative;
}

.did-floating-label {
    color: #1e4c82;
    font-size: 15px;
    position: absolute;
    pointer-events: none;
    left: 8.2px;
    top: 11px;
    padding: 0 5px;
    background: #fff;
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
}

.did-floating-input,
.did-floating-select {
    font-size: 13px;
    display: block;
    width: 100%;
    height: 34px;
    padding: 8px 13px;
    padding-right: 19px;
    background: #fff;
    color: #6c757d;
    border: 1px solid #adb5bd;
    border-radius: 4px;
    box-sizing: border-box;

    &:focus {
        outline: 1.5px solid #0d6efd;

        ~.did-floating-label {
            color: #0d6efd;
            top: -8px;
            font-size: 11px;
        }
    }
}

select.did-floating-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

select.did-floating-select::-ms-expand {
    display: none;
}

.did-floating-input:not(:placeholder-shown)~.did-floating-label {
    top: -7.5px;
    font-size: 11px;
}

.did-floating-select:not([value=""]):valid~.did-floating-label {
    top: -7.5px;
    font-size: 11px;
}

.did-floating-select[value=""]:focus~.did-floating-label {
    top: 11px;
    font-size: 13px;
}

.did-floating-select:not([multiple]):not([size]) {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='6' viewBox='0 0 8 6'%3E%3Cpath id='Path_1' data-name='Path 1' d='M371,294l4,6,4-6Z' transform='translate(-371 -294)' fill='%23003d71'/%3E%3C/svg%3E%0A");
    background-position: right 15px top 50%;
    background-repeat: no-repeat;
}

.userType-width {
    width: 65%;
}

.accountStatus-width {
    width: 75%;
}

.filter-container {
    width: 60%;
}

@media (max-width: 767px) {
    .userType-width {
        width: 100%;
    }

    .accountStatus-width {
        width: 100%;
    }

    .filter-container {
        width: 100%;
    }
}
</style>
